import { Check, ChevronDown, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Vertical year-scroll picker. Used everywhere we need a 4-digit year
 * (build year, owned since, installed year, etc.) so the user can't
 * type "20243" or "abc" — we own the input and they just scroll-and-tap.
 *
 * Self-future-proofing: `maxYear` defaults to currentYear + 1, computed
 * at render-time from `new Date()`. No developer maintenance — every
 * January the upper bound just advances on its own.
 *
 * Layout:
 *   - Tap the trigger button (looks like an input) → modal sheet
 *   - Sheet shows a vertical scroll list of years (newest at top)
 *   - Current selection is highlighted forest; tap any to pick + close
 *   - Long lists auto-scroll so the current value is visible on open
 *
 * Compared to a wheel-style spinner, this is simpler to implement,
 * matches the rest of the app's bottom-sheet idiom, and works
 * identically on web + iOS + Android with no extra deps.
 */

const ROW_HEIGHT = 44;

interface YearPickerProps {
  /** Currently selected year, or null when blank. */
  value: number | null;
  /** Called when the user picks a year. */
  onChange: (year: number | null) => void;
  /** Trigger-button label / placeholder when value is null. */
  placeholder?: string;
  /** Earliest selectable year. Defaults to 1850 — old enough for any
   *  US home build year, recent enough to keep the list scannable.
   *  Pass `1950` or higher for "owned since" fields. */
  minYear?: number;
  /** Latest selectable year. Defaults to currentYear + 1 so users can
   *  pick the upcoming year (e.g. "owned since 2027" if they buy in
   *  late 2026). Always recomputed at render — no annual maintenance. */
  maxYear?: number;
  /** Suffix on the trigger button when value is null, e.g. "Optional". */
  optional?: boolean;
  /** Sheet title (default: "Pick a year"). */
  title?: string;
  /** Tap-out callback for clearing the field. If omitted, no clear button. */
  allowClear?: boolean;
}

export function YearPicker({
  value,
  onChange,
  placeholder = 'Pick a year',
  minYear = 1850,
  maxYear,
  optional,
  title = 'Pick a year',
  allowClear = true,
}: YearPickerProps) {
  const [open, setOpen] = useState(false);

  // Year ranges are computed at render time so we always include the
  // upcoming year without a manual bump every January.
  const currentYear = new Date().getFullYear();
  const effectiveMax = maxYear ?? currentYear + 1;

  // Newest year first so the most-likely picks (recent install years,
  // recent purchases) are at the top of the list.
  const years: number[] = [];
  for (let y = effectiveMax; y >= minYear; y--) years.push(y);

  // One styled picker for ALL platforms (Wave 47). Web previously used
  // an invisible native <select> for keyboard nav, but the open list is
  // OS-rendered — a bare white/blue browser listbox that can't be
  // themed and looked broken next to the rest of the app. The sheet
  // auto-scrolls to the current value, so the speed loss is minimal.
  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={value ? `${title}: ${value}` : `${title} — not set`}
        style={({ pressed }) => [
          styles.trigger,
          pressed && { opacity: 0.85 },
        ]}>
        <Text style={[styles.triggerText, value == null && styles.triggerPlaceholder]}>
          {value != null ? String(value) : `${placeholder}${optional ? ' · optional' : ''}`}
        </Text>
        <ChevronDown size={16} color={Palette.inkSoft} strokeWidth={2} />
      </Pressable>

      <YearPickerSheet
        visible={open}
        years={years}
        value={value}
        title={title}
        allowClear={allowClear}
        onPick={(y) => {
          onChange(y);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function YearPickerSheet({
  visible,
  years,
  value,
  title,
  allowClear,
  onPick,
  onClose,
}: {
  visible: boolean;
  years: number[];
  value: number | null;
  title: string;
  allowClear: boolean;
  onPick: (year: number | null) => void;
  onClose: () => void;
}) {
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView | null>(null);
  // Desktop (wide) presentation: a centered card like the "Your homes"
  // popup, instead of a bottom sheet pinned to the screen edge — a
  // sheet sliding to the bottom of a 1400px window reads as misplaced.
  const { width } = useWindowDimensions();
  const centered = width >= 768;

  // When the sheet opens, jump the list so the current value is roughly
  // centered. Falls back to "around currentYear" when value is null so
  // the user lands near recent years, not at 1850.
  useEffect(() => {
    if (!visible) return;
    const target = value ?? new Date().getFullYear();
    const index = years.indexOf(target);
    if (index < 0) return;
    // Defer a tick so the ScrollView measures itself first.
    const t = setTimeout(() => {
      scrollRef.current?.scrollTo({
        y: Math.max(0, index * ROW_HEIGHT - 4 * ROW_HEIGHT),
        animated: false,
      });
    }, 30);
    return () => clearTimeout(t);
  }, [visible, value, years]);

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}>
      <Pressable
        style={[styles.backdrop, centered && styles.backdropCentered]}
        onPress={onClose}>
        <Pressable
          style={[
            styles.sheet,
            centered
              ? styles.sheetCentered
              : { paddingBottom: Math.max(28, insets.bottom + 20) },
          ]}
          onPress={(e) => e.stopPropagation()}>
          {!centered ? <View style={styles.handle} /> : null}
          <View style={styles.head}>
            <Text style={styles.title}>{title}</Text>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              accessibilityLabel="Close"
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={16} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <ScrollView
            ref={scrollRef}
            style={styles.list}
            showsVerticalScrollIndicator={false}>
            {years.map((y) => {
              const sel = y === value;
              return (
                <Pressable
                  key={y}
                  onPress={() => onPick(y)}
                  style={({ pressed }) => [
                    styles.row,
                    sel && styles.rowSel,
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Text style={[styles.rowText, sel && styles.rowTextSel]}>{y}</Text>
                  {sel ? (
                    <View style={styles.rowCheck}>
                      <Check size={16} color={Palette.cream} strokeWidth={2.4} />
                    </View>
                  ) : null}
                </Pressable>
              );
            })}
          </ScrollView>

          {allowClear && value != null ? (
            <Pressable
              onPress={() => onPick(null)}
              style={({ pressed }) => [
                styles.clearBtn,
                pressed && { opacity: 0.85 },
              ]}
              accessibilityLabel="Clear year">
              <Text style={styles.clearBtnText}>Clear</Text>
            </Pressable>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 13,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  triggerText: {
    fontFamily: Fonts.sansBold,
    fontSize: 15,
    color: Palette.ink,
  },
  triggerPlaceholder: {
    color: Palette.inkFaint,
    fontFamily: Fonts.sansMedium,
  },

  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(42,37,31,0.45)',
    justifyContent: 'flex-end',
  },
  // Desktop: dialog floats mid-screen like the Your-homes popup.
  backdropCentered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  sheet: {
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: Spacing.four,
    paddingTop: 12,
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -6 },
    shadowOpacity: 0.18,
    shadowRadius: 22,
    elevation: 12,
  },
  // Centered (desktop) variant: kill the sheet's shadow + elevation —
  // the elevation/offset combo rendered as a solid black band hanging
  // below the card on web. The dimmed backdrop already separates the
  // dialog; a hairline border does the rest.
  sheetCentered: {
    borderRadius: 24,
    maxWidth: 420,
    paddingBottom: 20,
    paddingTop: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  },
  handle: {
    alignSelf: 'center',
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: Palette.sandDeep,
    marginBottom: 12,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.three,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 20,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    maxHeight: 380,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    // Center the year text; the check (when selected) absolutes itself
    // to the right edge so the year always sits visually in the middle
    // of the row rather than left-aligned beside a vertical gap.
    justifyContent: 'center',
    height: ROW_HEIGHT,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    position: 'relative',
  },
  rowSel: {
    backgroundColor: Palette.forest,
    borderBottomColor: Palette.forest,
  },
  rowText: {
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  rowTextSel: {
    color: Palette.cream,
  },
  rowCheck: {
    position: 'absolute',
    right: 18,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  clearBtn: {
    marginTop: 12,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
  },
  clearBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.clay,
    letterSpacing: 0.3,
  },
});
